import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, TrendingUp, TrendingDown, Wallet, AlertTriangle, ChevronDown,
  ChevronUp, Loader2, ArrowRight, Coffee, Info, Lightbulb, Receipt, Repeat,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../ui/button";
import api from "../../lib/api";

const STORAGE_KEY = "mg_weekly_brief_collapsed";

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};

const fmtRange = (startIso, endIso) => {
  if (!startIso || !endIso) return "";
  const s = new Date(startIso);
  const e = new Date(endIso);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  return sameMonth
    ? `${s.getDate()}–${e.getDate()} ${e.toLocaleDateString("es-ES", { month: "short" })}`
    : `${fmtDate(startIso)} – ${fmtDate(endIso)}`;
};

export default function WeeklyBrief({ restaurantId }) {
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default collapsed state: open on Mondays, closed otherwise; persist user choice per day
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      const todayKey = new Date().toISOString().slice(0, 10);
      if (saved.day === todayKey && typeof saved.collapsed === "boolean") {
        return saved.collapsed;
      }
    } catch { /* ignore */ }
    return new Date().getDay() !== 1;   // Monday = 1, open by default
  });

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const b = await api.getWeeklyBrief(restaurantId);
      setBrief(b);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        day: new Date().toISOString().slice(0, 10),
        collapsed: next,
      }));
    } catch { /* ignore */ }
  };

  if (loading && !brief) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-sm text-muted-foreground">Preparando tu brief semanal…</span>
      </div>
    );
  }
  if (!brief) return null;

  const rev = brief.revenue || {};
  const cf = brief.cash_flow || {};
  const payment = brief.next_critical_payment;
  const insight = brief.top_insight;

  const revUp = (rev.delta_pct ?? 0) >= 0;
  const cfOk = !cf.alert_triggered;
  const dupPrev = brief.duplicates_prevented || {};

  return (
    <div
      className="relative rounded-2xl border border-indigo-200 overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-violet-50 shadow-sm"
      data-testid="weekly-brief-banner"
    >
      {/* Decorative top accent */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />

      {/* Header */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white/40 transition text-left"
        data-testid="weekly-brief-toggle"
      >
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0 shadow-sm">
          {brief.is_monday
            ? <Coffee className="h-5 w-5 text-white" />
            : <Sparkles className="h-5 w-5 text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-[15px] font-bold tracking-tight text-foreground">
              Tu semana en 60 segundos
            </h2>
            <span className="text-[11px] font-medium text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
              {brief.iso_week}
            </span>
            {brief.is_monday && (
              <span className="text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                Lunes · buen día para revisar
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Resumen ejecutivo · {fmtRange(brief.week_of, brief.week_end)}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-1 text-slate-400 hover:text-slate-700">
          {collapsed
            ? <ChevronDown className="h-5 w-5" />
            : <ChevronUp className="h-5 w-5" />}
        </div>
      </button>

      {!collapsed && (
        <div className="px-5 pb-5 space-y-4" data-testid="weekly-brief-body">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {/* Revenue */}
            <Tile
              tone={revUp ? "emerald" : "amber"}
              icon={revUp ? TrendingUp : TrendingDown}
              label="Ingresos 7d"
              testid="brief-revenue"
              onClick={() => navigate("/app/finance")}
            >
              <div className="text-2xl font-bold">{fmtEUR(rev.this_week)}</div>
              <div className="text-[11px] opacity-80 mt-0.5">
                {rev.delta_pct === null
                  ? `${rev.this_tickets} tickets`
                  : (
                    <>
                      <b>{revUp ? "+" : ""}{rev.delta_pct}%</b> vs. semana pasada
                      <span className="opacity-70"> · {rev.this_tickets} tickets</span>
                    </>
                  )}
              </div>
            </Tile>

            {/* Cash flow */}
            <Tile
              tone={cfOk ? "sky" : "red"}
              icon={cf.alert_triggered ? AlertTriangle : Wallet}
              label="Previsión 14d"
              testid="brief-cashflow"
              onClick={() => navigate("/app/finance?tab=forecast")}
            >
              <div className="text-2xl font-bold">{fmtEUR(cf.min_balance)}</div>
              <div className="text-[11px] opacity-80 mt-0.5">
                saldo mín. <b>{fmtDate(cf.min_balance_date)}</b>
                {cf.alert_triggered && <span className="font-semibold"> · bajo umbral</span>}
              </div>
            </Tile>

            {/* Next critical payment */}
            <Tile
              tone="slate"
              icon={payment?.type === "recurring" ? Repeat : Receipt}
              label="Próximo pago"
              testid="brief-payment"
              onClick={() => navigate("/app/finance?tab=forecast")}
            >
              {payment ? (
                <>
                  <div className="text-2xl font-bold truncate">
                    -{fmtEUR(payment.amount)}
                  </div>
                  <div className="text-[11px] opacity-80 mt-0.5 truncate">
                    {payment.label} · <b>{fmtDate(payment.date)}</b>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-lg font-semibold text-emerald-700">Todo pagado</div>
                  <div className="text-[11px] opacity-80 mt-0.5">
                    Sin pagos previstos 14d
                  </div>
                </>
              )}
            </Tile>

            {/* Top AI insight */}
            <Tile
              tone="violet"
              icon={insight?.type === "warning" || insight?.type === "alert"
                ? AlertTriangle
                : insight?.type === "tip" || insight?.type === "recommendation"
                  ? Lightbulb
                  : Info}
              label="Insight del asesor"
              testid="brief-insight"
              onClick={() => navigate("/app/finance")}
            >
              {insight ? (
                <>
                  <div className="text-sm font-semibold line-clamp-2 leading-tight">
                    {insight.title}
                  </div>
                  {insight.body && (
                    <div className="text-[11px] opacity-80 mt-1 line-clamp-2 leading-snug">
                      {insight.body}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs opacity-80">
                  Los insights aparecerán cuando tengas facturas y ventas registradas esta semana.
                </div>
              )}
            </Tile>
          </div>

          {dupPrev.count > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs"
              data-testid="brief-duplicates-prevented"
            >
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-700" />
              <span>
                Esta semana te hemos evitado{" "}
                <strong>{dupPrev.count} duplicado{dupPrev.count === 1 ? "" : "s"}</strong>
                {dupPrev.total_amount > 0 && <> por valor de <strong>{fmtEUR(dupPrev.total_amount)}</strong></>}.
                {" "}Menos ruido en tu P&amp;L.
              </span>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-[11px] text-muted-foreground italic">
              Generado a partir de tus últimos 90 días · se actualiza en tiempo real
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={() => navigate("/app/finance")}
              data-testid="brief-go-to-finance"
            >
              Ver detalle completo <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ tone, icon: Icon, label, testid, onClick, children }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    sky:     "bg-sky-50 border-sky-200 text-sky-900",
    red:     "bg-red-50 border-red-200 text-red-900",
    slate:   "bg-card border-border text-foreground",
    violet:  "bg-violet-50 border-violet-200 text-violet-900",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${tones[tone]} rounded-xl border p-3 text-left hover:shadow-md hover:-translate-y-0.5 transition group`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1 opacity-80">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      {children}
    </button>
  );
}
